import { useMemoizedFn } from "ahooks";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  SHEET_PULL_DOWN_SLOP_PX,
  sheetPullDownGestureDecision,
} from "./sheetPullDownGestureDecision";

export interface UseDraggableOptions {
  enabled?: boolean; // 是否启用拖拽功能
  onClose?: () => void;
  threshold?: number; // 触发关闭的距离阈值（px），默认 200
  /** 与 Drawer open 同步；仅在变为 true（再次打开）时重置拖拽态，避免 onClose 瞬间把 dragDistance 置 0 导致底部闪一下 */
  isOpen?: boolean;
  /**
   * H5 底部 sheet：从「canonical 列表滚动容器」下拉关闭。
   * 仅当容器 scrollTop===0 且下移超过 slop 才接管（档位 B）；顶部拖条逻辑不变。
   */
  listPullDownFromScrollRoot?: {
    getScrollRoot: () => HTMLElement | null;
    dependencyKey: number;
  };
  swipeToClose?: boolean;
  dragThreshold?: number;
}

export interface UseDraggableReturn {
  isDragging: boolean;
  /** 松手/点击后正在播放「滑出再卸载」的关闭动画 */
  isDragCloseAnimating: boolean;
  dragDistance: number;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleDragBarClick: () => void;
}

/** 超过阈值后：先滑出再 onClose，时长需与 Drawer wrapper transition 一致 */
export const DRAG_CLOSE_ANIM = {
  durationMs: 550,
  durationS: 0.55,
  /** ease-in，比瞬间 onClose 更易被感知为「收合」 */
  easing: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

function getOffScreenDragPx(): number {
  if (typeof window === "undefined") {
    return 2000;
  }
  return Math.max(window.innerHeight, window.innerWidth) + 100;
}

/**
 * 拖拽功能 Hook
 * 用于实现抽屉的拖拽关闭功能
 */
export const useDraggable = ({
  enabled = true,
  onClose,
  threshold = 200,
  isOpen = true,
  listPullDownFromScrollRoot,
}: UseDraggableOptions): UseDraggableReturn => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDragCloseAnimating, setIsDragCloseAnimating] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const dragStartYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const dragDistanceRef = useRef(0);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** 防止超阈值松手后拖条再触发一次 click 导致重复关窗 */
  const isCloseSequenceActiveRef = useRef(false);

  const clearCloseTimer = useMemoizedFn(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  });

  const startCloseAnimation = useCallback(() => {
    if (isCloseSequenceActiveRef.current) {
      return;
    }
    isCloseSequenceActiveRef.current = true;
    clearCloseTimer();
    isDraggingRef.current = false;
    setIsDragging(false);
    setIsDragCloseAnimating(true);
    const off = getOffScreenDragPx();
    dragDistanceRef.current = off;
    setDragDistance(off);
    closeTimerRef.current = setTimeout(() => {
      onClose?.();
      // 不在此处把 dragDistance 置 0：会与 onClose → open=false 同一轮更新竞态，
      // wrapper 瞬间回到满高，底部出现一条闪动；距离在 isOpen 再次为 true 时重置
      setIsDragCloseAnimating(false);
      closeTimerRef.current = null;
      isCloseSequenceActiveRef.current = false;
    }, DRAG_CLOSE_ANIM.durationMs);
  }, [clearCloseTimer, onClose]);

  // 拖动开始处理
  const handleDragStart = useCallback(
    (clientY: number) => {
      if (!enabled) {
        return;
      }

      clearCloseTimer();
      isCloseSequenceActiveRef.current = false;
      setIsDragCloseAnimating(false);
      isDraggingRef.current = true;
      dragStartYRef.current = clientY;
      setIsDragging(true);
      setDragDistance(0);
      dragDistanceRef.current = 0;
    },
    [clearCloseTimer, enabled],
  );

  // 拖动过程处理
  const handleDragMove = useCallback((clientY: number) => {
    // 关闭动画期间不再响应拖动，避免出现反向拖拽/抖动
    if (!isDraggingRef.current || isCloseSequenceActiveRef.current) {
      return;
    }

    const distance = clientY - dragStartYRef.current;

    // 只允许向下拖动
    if (distance > 0) {
      dragDistanceRef.current = distance;
      setDragDistance(distance);
      return;
    }

    // 向上拖动（distance <= 0）时直接回到 0，避免残留上一帧正值造成“还能向上拖”的错觉
    if (dragDistanceRef.current !== 0) {
      dragDistanceRef.current = 0;
      setDragDistance(0);
    }
  }, []);

  // 拖动结束处理
  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;
    setIsDragging(false);

    const d = dragDistanceRef.current;
    if (d > threshold) {
      startCloseAnimation();
    } else {
      dragDistanceRef.current = 0;
      setDragDistance(0);
    }
  }, [startCloseAnimation, threshold]);

  // 鼠标事件处理
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled) {
        return;
      }

      e.preventDefault();
      handleDragStart(e.clientY);
    },
    [enabled, handleDragStart],
  );

  // 触摸事件处理
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) {
        return;
      }

      // 阻止默认行为，防止触发页面滚动
      e.preventDefault();
      handleDragStart(e.touches[0].clientY);
    },
    [enabled, handleDragStart],
  );

  // 点击拖动条关闭 — 与超阈值同样走滑出再 onClose
  const handleDragBarClick = useCallback(() => {
    if (!enabled) {
      return;
    }

    setTimeout(() => {
      if (!isDraggingRef.current) {
        startCloseAnimation();
      }
    }, 0);
  }, [enabled, startCloseAnimation]);

  // 监听全局鼠标移动和释放
  useEffect(() => {
    // 如果未启用或不在拖拽状态，不添加事件监听
    if (!enabled || !isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    const handleTouchMove = (e: TouchEvent) => {
      // 阻止默认的滚动行为，只在拖动时有效
      e.preventDefault();
      handleDragMove(e.touches[0].clientY);
    };

    const handleTouchEnd = () => {
      handleDragEnd();
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    // 使用 passive: false 允许 preventDefault 生效
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [enabled, isDragging, handleDragMove, handleDragEnd]);

  /** canonical 列表顶下拉（档位 B）：touchstart 不设 pending；touchmove 再判定 scrollTop / slop */
  useEffect(() => {
    const cfg = listPullDownFromScrollRoot;
    if (!enabled || !cfg || !isOpen) {
      return;
    }

    const root = cfg.getScrollRoot();
    if (!root) {
      return;
    }

    let pendingStartY: number | null = null;

    const onTouchStart = (e: TouchEvent) => {
      if (
        !enabled ||
        isDraggingRef.current ||
        isCloseSequenceActiveRef.current
      ) {
        return;
      }
      const el = cfg.getScrollRoot();
      if (!el || el.scrollTop > 0) {
        pendingStartY = null;
        return;
      }
      pendingStartY = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      const el = cfg.getScrollRoot();
      if (!el) {
        pendingStartY = null;
        return;
      }

      if (isDraggingRef.current) {
        return;
      }

      if (pendingStartY === null) {
        return;
      }

      const y = e.touches[0].clientY;
      const dy = y - pendingStartY;

      switch (
        sheetPullDownGestureDecision({
          scrollTop: el.scrollTop,
          dy,
          slopPx: SHEET_PULL_DOWN_SLOP_PX,
        })
      ) {
        case "abort-pending":
          pendingStartY = null;
          return;
        case "keep-pending":
          return;
        case "commit":
          e.preventDefault();
          handleDragStart(pendingStartY);
          pendingStartY = null;
          handleDragMove(y);
          return;
      }
    };

    const onTouchEndOrCancel = () => {
      pendingStartY = null;
    };

    root.addEventListener("touchstart", onTouchStart, { passive: true });
    root.addEventListener("touchmove", onTouchMove, { passive: false });
    root.addEventListener("touchend", onTouchEndOrCancel);
    root.addEventListener("touchcancel", onTouchEndOrCancel);

    return () => {
      root.removeEventListener("touchstart", onTouchStart);
      root.removeEventListener("touchmove", onTouchMove);
      root.removeEventListener("touchend", onTouchEndOrCancel);
      root.removeEventListener("touchcancel", onTouchEndOrCancel);
    };
  }, [
    enabled,
    isOpen,
    listPullDownFromScrollRoot?.dependencyKey,
    handleDragStart,
    handleDragMove,
    listPullDownFromScrollRoot,
  ]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    clearCloseTimer();
    isCloseSequenceActiveRef.current = false;
    setIsDragCloseAnimating(false);
    dragDistanceRef.current = 0;
    setDragDistance(0);
  }, [isOpen, clearCloseTimer]);

  return {
    isDragging,
    isDragCloseAnimating,
    dragDistance,
    handleMouseDown,
    handleTouchStart,
    handleDragBarClick,
  };
};
